import { Box, Typography, Divider, useTheme, alpha } from "@mui/material";
import React from "react";
import { getCadence } from "lib/cadence";

// Emoji, not flagcdn.com images: no third-party request to break or leak to,
// and no broken-image icon when it's unreachable.
const flagMap = {
  USD: "🇺🇸",
  CAD: "🇨🇦",
  GBP: "🇬🇧",
};

const StatLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: "0.78rem",
      fontWeight: 600,
      color: "text.secondary",
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
);

const StatValue = ({ children, size = "normal", color }) => (
  <Typography
    sx={{
      fontSize:
        size === "hero" ? "1.75rem" : size === "large" ? "1.1rem" : "0.95rem",
      fontWeight: size === "hero" ? 700 : 600,
      color: color || "text.primary",
      letterSpacing: size === "hero" ? "-0.02em" : "normal",
      lineHeight: 1.2,
    }}
  >
    {children}
  </Typography>
);

function formatCurrency(amount, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
  }).format(amount);
}

function GroupData({ group, memberCount = 0 }) {
  const theme = useTheme();
  const flagSrc = flagMap[group.currency] || "";
  const minPool = Math.max(
    group.monthlyContribution * (group.minMembers - 1),
    0,
  );
  const maxPool = Math.max(
    group.monthlyContribution * (group.maxMembers - 1),
    0,
  );
  const currentPayout =
    group.monthlyContribution * Math.max(memberCount - 1, 0);
  const cadence = getCadence(group.intervalUnit, group.intervalCount);

  return (
    <Box>
      {/* Hero stat — monthly contribution */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 3,
          // Both children can wrap below each other on narrow phones — their
          // min-content width otherwise blows the card out past the viewport.
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <StatLabel>{cadence.adjective} Contribution</StatLabel>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
            <StatValue size="hero" color="primary.main">
              {formatCurrency(group.monthlyContribution, group.currency)}
            </StatValue>
            <Typography
              sx={{
                fontSize: "0.85rem",
                color: "text.secondary",
                fontWeight: 400,
              }}
            >
              {cadence.suffix}
            </Typography>
          </Box>
        </Box>

        <Divider orientation="vertical" flexItem />

        <Box sx={{ flex: 1, minWidth: 100 }}>
          <StatLabel>Currency</StatLabel>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {flagSrc && (
              <Typography
                component="span"
                sx={{ fontSize: "1.1rem", lineHeight: 1 }}
                aria-hidden
              >
                {flagSrc}
              </Typography>
            )}
            <StatValue size="large">{group.currency}</StatValue>
          </Box>
        </Box>
      </Box>

      {/* Stats grid — stacks on phones; the uppercase labels' min-content
          width overflows 390px viewports in a rigid three-column grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            px: 2,
            py: 2,
            borderRadius: 2.5,
            bgcolor: "background.default",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <StatLabel>Participants</StatLabel>
          <StatValue>
            {group.minMembers === group.maxMembers
              ? group.maxMembers
              : `${group.minMembers}–${group.maxMembers}`}
          </StatValue>
          <Typography
            sx={{ fontSize: "0.72rem", color: "text.secondary", mt: 0.25 }}
          >
            {group.minMembers === group.maxMembers ? "members" : "min–max"}
          </Typography>
        </Box>

        <Box
          sx={{
            minWidth: 0,
            px: 2,
            py: 2,
            borderRadius: 2.5,
            bgcolor: "background.default",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <StatLabel>Payout Range</StatLabel>
          <StatValue color="success.main">
            {formatCurrency(minPool, group.currency)}
          </StatValue>
          <Typography
            sx={{ fontSize: "0.72rem", color: "text.secondary", mt: 0.25 }}
          >
            {minPool === maxPool
              ? "per turn"
              : `up to ${formatCurrency(maxPool, group.currency)}`}
          </Typography>
        </Box>

        <Box
          sx={{
            minWidth: 0,
            px: 2,
            py: 2,
            borderRadius: 2.5,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          }}
        >
          <StatLabel>Current Payout</StatLabel>
          <StatValue color="primary.main">
            {formatCurrency(currentPayout, group.currency)}
          </StatValue>
          <Typography
            sx={{ fontSize: "0.72rem", color: "text.secondary", mt: 0.25 }}
          >
            {memberCount} member{memberCount !== 1 ? "s" : ""}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default GroupData;
